import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  CloudDownload,
  Download,
  Trash2,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Estado } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { mesEnLetras } from "@/lib/periodo";

export const Route = createFileRoute("/_app/tipo-cambio")({
  component: TipoDeCambio,
});

// ===========================================================================
// El tipo de cambio del periodo.
//
// La contabilidad se lleva en soles, así que todo movimiento en dólares
// necesita su equivalente para el asiento. El camino corto es traerlo de la
// SUNAT, que es la fuente que manda. Los otros dos siguen estando para cuando
// el portal no conteste o el cambio no sea el publicado: la hoja de cálculo
// con los días ya puestos, y escribir el día suelto a mano.
//
// Solo se piden los días que TIENEN movimientos en dólares. La SUNAT no
// publica sábados ni feriados, y pedir un cambio para un día sin un solo
// movimiento en dólares es pedir un dato que nadie puede dar.
// ===========================================================================

function TipoDeCambio() {
  const { data, isPending } = api.tipoCambio.useQuery(["tipo-cambio"]);
  const d = data?.status === 200 ? data.body : null;

  const qc = useQueryClient();
  const guardar = api.guardarTipoCambio.useMutation();
  const borrar = api.borrarTipoCambio.useMutation();

  const [subiendo, setSubiendo] = useState(false);
  const [trayendo, setTrayendo] = useState(false);
  const archivo = useRef<HTMLInputElement>(null);

  /**
   * Traer de la SUNAT lo que falte.
   *
   * Lo que la SUNAT no publica —sábados, domingos y feriados— se dice con su
   * fecha: es lo que separa «el portal no tiene ese día» de «el sistema no lo
   * pidió», que son dos problemas distintos y se arreglan de otra manera.
   */
  const traerDeSunat = async () => {
    setTrayendo(true);
    try {
      const r = await fetch("/sunat/tipo-cambio", {
        method: "POST",
        credentials: "include",
      });
      const j = (await r.json()) as {
        guardados?: number;
        faltan?: number;
        sinPublicar?: string[];
        descartados?: Array<{ fecha: string; motivo: string }>;
        message?: string;
      };

      if (!r.ok) {
        toast.error(j.message ?? "No se pudo traer el tipo de cambio", {
          description:
            "La plantilla en Excel sigue estando para salir del paso",
          duration: 10_000,
        });
        return;
      }

      const sinPublicar = j.sinPublicar ?? [];
      const descartados = j.descartados ?? [];

      if (!j.guardados && !j.faltan) {
        toast.success("No falta ningún día");
        return;
      }

      toast.success(`${j.guardados ?? 0} días traídos de la SUNAT`, {
        description: descartados.length
          ? `${descartados.length} sin usar · ${descartados[0]?.fecha}: ${descartados[0]?.motivo}`
          : sinPublicar.length
            ? `${sinPublicar.length} días sin publicar (sábados, domingos o feriados): ${sinPublicar
                .slice(0, 4)
                .map(fecha)
                .join(", ")}${sinPublicar.length > 4 ? "…" : ""}`
            : undefined,
        duration: sinPublicar.length || descartados.length ? 12_000 : 5_000,
      });
      qc.invalidateQueries();
    } catch {
      toast.error("No se pudo hablar con la SUNAT");
    } finally {
      setTrayendo(false);
    }
  };

  const subir = async (f: File) => {
    setSubiendo(true);
    try {
      const cuerpo = new FormData();
      cuerpo.append("archivo", f);
      const r = await fetch("/plantilla/tipo-cambio", {
        method: "POST",
        body: cuerpo,
        credentials: "include",
      });
      const j = (await r.json()) as {
        guardados?: number;
        rechazadas?: Array<{ fila: number; motivo: string }>;
        deMas?: number;
        message?: string;
      };

      if (!r.ok) {
        toast.error(j.message ?? "No se pudo leer el archivo");
        return;
      }

      // Lo que no entró se dice con su número de fila: es lo que hace falta
      // para arreglarlo sin adivinar cuál de las treinta filas estaba mal.
      const problemas = j.rechazadas ?? [];
      toast.success(`${j.guardados ?? 0} días guardados`, {
        description: problemas.length
          ? `${problemas.length} filas sin guardar · fila ${problemas
              .slice(0, 3)
              .map((p) => `${p.fila} (${p.motivo})`)
              .join(", fila ")}`
          : j.deMas
            ? `${j.deMas} de ellos no tienen movimientos en dólares`
            : undefined,
        duration: problemas.length ? 12_000 : 5_000,
      });
      qc.invalidateQueries();
    } catch {
      toast.error("No se pudo subir el archivo");
    } finally {
      setSubiendo(false);
      if (archivo.current) archivo.current.value = "";
    }
  };

  return (
    <>
      <Cabecera
        migas={["Maestros"]}
        titulo="Tipo de cambio"
        acciones={
          <>
            <Button size="sm" variant="outline" asChild>
              <a href="/plantilla/tipo-cambio.xlsx" download>
                <Download className="mr-2 size-4" />
                Descargar la plantilla
              </a>
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={subiendo}
              onClick={() => archivo.current?.click()}
            >
              <Upload className="mr-2 size-4" />
              {subiendo ? "Leyendo…" : "Subir la plantilla"}
            </Button>
            <Button
              size="sm"
              disabled={trayendo}
              onClick={() => void traerDeSunat()}
            >
              <CloudDownload className="mr-2 size-4" />
              {trayendo ? "Preguntando a la SUNAT…" : "Traer de la SUNAT"}
            </Button>
            <input
              ref={archivo}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void subir(f);
              }}
            />
          </>
        }
      />

      <p className="-mt-2 max-w-3xl text-[13px] text-muted-foreground">
        La contabilidad se lleva en soles, así que cada movimiento en dólares
        necesita el cambio de su día para el asiento. Se traen de la SUNAT, que
        es la fuente que manda, y solo los días con movimientos en dólares: la
        SUNAT no publica sábados ni feriados. Lo que ya esté puesto a mano no se
        pisa.
      </p>

      {d && d.faltan > 0 ? (
        <Aviso tono="warn">
          <AlertTriangle className="mt-px size-4 shrink-0" />
          <span>
            <b>Faltan {d.faltan} días</b> — sin ellos no se puede cerrar{" "}
            {mesEnLetras(d.periodo)}, porque sus movimientos en dólares no
            tienen equivalente en soles.
          </span>
        </Aviso>
      ) : null}

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="font-medium text-base">
            Días con movimientos en dólares
          </CardTitle>
          <span className="text-[11px] text-muted-foreground">
            compra y venta, seis decimales
          </span>
        </CardHeader>

        <CardContent className="px-0">
          {isPending ? (
            <div className="space-y-2 px-6">
              {["a", "b", "c"].map((k) => (
                <Skeleton key={k} className="h-10" />
              ))}
            </div>
          ) : !d || d.dias.length === 0 ? (
            <p className="px-6 py-8 text-center text-muted-foreground text-sm">
              No hay ningún movimiento en dólares en este periodo, así que no
              hace falta ningún tipo de cambio.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="py-2 pl-6 text-left font-medium">Día</th>
                    <th className="py-2 text-right font-medium">Movimientos</th>
                    <th className="py-2 pl-8 text-left font-medium">Compra</th>
                    <th className="py-2 pl-6 text-left font-medium">Venta</th>
                    <th className="py-2 pl-6 text-left font-medium">
                      De dónde salió
                    </th>
                    <th className="py-2 pr-6" />
                  </tr>
                </thead>
                <tbody>
                  {d.dias.map((dia: (typeof d.dias)[number]) => (
                    <Fila
                      key={dia.fecha}
                      dia={dia}
                      alGuardar={(compra, venta) =>
                        guardar.mutate(
                          { body: { fecha: dia.fecha, compra, venta } },
                          {
                            onSuccess: (r: {
                              status: number;
                              body: { message?: string };
                            }) => {
                              if (r.status !== 200) {
                                toast.error(
                                  r.body.message ?? "No se pudo guardar",
                                );
                                return;
                              }
                              toast.success(`Guardado el ${fecha(dia.fecha)}`);
                              qc.invalidateQueries();
                            },
                            onError: () => toast.error("No se pudo guardar"),
                          },
                        )
                      }
                      alBorrar={() =>
                        borrar.mutate(
                          { body: { fecha: dia.fecha } },
                          {
                            onSuccess: () => {
                              toast.success("Quitado");
                              qc.invalidateQueries();
                            },
                          },
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function Fila({
  dia,
  alGuardar,
  alBorrar,
}: {
  dia: {
    fecha: string;
    compra: string | null;
    venta: string | null;
    fuente: string | null;
    movimientosUsd: number;
  };
  alGuardar: (compra: number, venta: number) => void;
  alBorrar: () => void;
}) {
  const [compra, setCompra] = useState(dia.compra ?? "");
  const [venta, setVenta] = useState(dia.venta ?? "");

  const cambiado = compra !== (dia.compra ?? "") || venta !== (dia.venta ?? "");
  const completo = compra.trim() !== "" && venta.trim() !== "";

  const guardar = () => {
    // La coma decimal se acepta aquí también: es lo que sale de un teclado en
    // español, y obligar al punto solo sirve para que el número se pierda.
    const c = Number(compra.replace(",", "."));
    const v = Number(venta.replace(",", "."));
    if (!Number.isFinite(c) || !Number.isFinite(v)) return;
    alGuardar(c, v);
  };

  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pl-6 font-medium tabular-nums">{fecha(dia.fecha)}</td>
      <td className="py-2 text-right text-muted-foreground tabular-nums">
        {dia.movimientosUsd}
      </td>
      <td className="py-2 pl-8">
        <input
          value={compra}
          onChange={(e) => setCompra(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && completo && guardar()}
          placeholder="3.750"
          inputMode="decimal"
          className="h-[30px] w-24 rounded-md border bg-background px-2 text-[12.5px] tabular-nums outline-none placeholder:text-muted-foreground/50"
        />
      </td>
      <td className="py-2 pl-6">
        <input
          value={venta}
          onChange={(e) => setVenta(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && completo && guardar()}
          placeholder="3.758"
          inputMode="decimal"
          className="h-[30px] w-24 rounded-md border bg-background px-2 text-[12.5px] tabular-nums outline-none placeholder:text-muted-foreground/50"
        />
      </td>
      <td className="py-2 pl-6">
        {dia.venta ? (
          <Estado tono="ok">{dia.fuente ?? "manual"}</Estado>
        ) : (
          <Estado tono="warn">falta</Estado>
        )}
      </td>
      <td className="py-2 pr-6 text-right">
        <div className="flex justify-end gap-1.5">
          {cambiado && completo ? (
            <Button size="sm" onClick={guardar}>
              <Check className="mr-1.5 size-3.5" />
              Guardar
            </Button>
          ) : null}
          {dia.venta ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={alBorrar}
              aria-label="Quitar"
            >
              <Trash2 className="size-3.5" />
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function fecha(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}-${m}-${a}`;
}
